import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  createTaxCode,
  createTaxTransaction,
  fetchTaxCodes,
  fetchTaxTransactions,
  fetchVatSummary,
  updateTaxCode,
} from "../../../api/accounting";
import { fetchBranches } from "../../../api/users";
import { formatCompactTZS, formatDate, formatTZS } from "../../../utils/format";
import CredoraIcon from "../Common/CredoraIcon";
import TablePageMetricCard from "../Common/TablePageMetricCard";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad, runRefresh } from "../Common/credoraToast";
import AccountingFilterApplyButton from "./AccountingFilterApplyButton";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { monthStartIsoDate, todayIsoDate, useLatestRef } from "./accountingUtils";

const TABS = [
  { id: "summary", label: "VAT summary" },
  { id: "codes", label: "Tax codes" },
  { id: "transactions", label: "Transactions" },
];

const TAX_TYPE_META = {
  vat: { label: "VAT", tone: "vat" },
  withholding: { label: "Withholding", tone: "withholding" },
  sales: { label: "Sales tax", tone: "sales" },
};

const TaxVatPage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [tab, setTab] = useState("summary");
  const [codes, setCodes] = useState([]);
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dateFrom, setDateFrom] = useState(monthStartIsoDate());
  const [dateTo, setDateTo] = useState(todayIsoDate());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [codeModal, setCodeModal] = useState(false);
  const [txnModal, setTxnModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [codeForm, setCodeForm] = useState({ code: "", name: "", tax_type: "vat", rate_pct: "18" });
  const [txnForm, setTxnForm] = useState({
    tax_code_id: "",
    txn_date: todayIsoDate(),
    reference: "",
    description: "",
    base_amount: "",
  });

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  const load = useCallback(() => {
    if (!branchId) return Promise.resolve();
    setLoading(true);
    setError("");
    return runLoad(
      () =>
        Promise.all([
          fetchTaxCodes(branchId).then(setCodes),
          fetchTaxTransactions(branchId).then(setTxns),
          fetchVatSummary(branchId, dateFrom, dateTo).then(setSummary),
        ]),
      { errorMessage: "Failed to load tax data." }
    )
      .catch((err) => setError(formatApiError(err, "Failed to load tax data.")))
      .finally(() => setLoading(false));
  }, [branchId, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const loadRef = useLatestRef(load);

  const branchName = useMemo(
    () => branches.find((b) => String(b.id) === String(branchId))?.name || "",
    [branches, branchId]
  );

  const activeCodes = useMemo(() => codes.filter((c) => c.is_active).length, [codes]);

  const effectiveRatePct = useMemo(() => {
    const base = Number(summary?.total_base || 0);
    const tax = Number(summary?.total_tax || 0);
    if (!base) return 0;
    return (tax / base) * 100;
  }, [summary]);

  const periodLabel = useMemo(() => {
    if (!summary) return `${formatDate(dateFrom)} – ${formatDate(dateTo)}`;
    if (summary.date_from && summary.date_to) {
      return `${formatDate(summary.date_from)} – ${formatDate(summary.date_to)}`;
    }
    return `${formatDate(dateFrom)} – ${formatDate(dateTo)}`;
  }, [summary, dateFrom, dateTo]);

  const deactivateCode = useCallback(
    (row) => {
      confirm({
        title: `Deactivate ${row.code}?`,
        message: "This tax code will no longer be available for new transactions.",
        confirmLabel: "Deactivate",
        tone: "danger",
        successMessage: "Tax code deactivated.",
        onConfirm: async () => {
          await updateTaxCode(row.id, { is_active: false });
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const codeColumns = useMemo(
    () => [
      {
        name: "Code",
        selector: (r) => r.code,
        width: "100px",
        cell: (r) => <span className="credora-tax-vat__code">{r.code}</span>,
      },
      { name: "Name", selector: (r) => r.name, grow: 2 },
      {
        name: "Type",
        selector: (r) => r.tax_type,
        width: "130px",
        cell: (r) => {
          const meta = TAX_TYPE_META[r.tax_type] || { label: r.tax_type, tone: "vat" };
          return <span className={`credora-tax-vat__type credora-tax-vat__type--${meta.tone}`}>{meta.label}</span>;
        },
      },
      {
        name: "Rate",
        selector: (r) => r.rate_pct,
        width: "90px",
        cell: (r) => <strong>{r.rate_pct}%</strong>,
      },
      {
        name: "Status",
        width: "100px",
        cell: (r) => (
          <span className={`credora-tax-vat__status credora-tax-vat__status--${r.is_active ? "active" : "inactive"}`}>
            {r.is_active ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        name: "",
        cell: (r) =>
          r.is_active ? (
            <AccountingIconActions
              actions={[
                {
                  icon: "clear",
                  label: "Deactivate tax code",
                  onClick: () => deactivateCode(r),
                  variant: "danger",
                },
              ]}
            />
          ) : null,
        width: "56px",
        ignoreRowClick: true,
      },
    ],
    [deactivateCode]
  );

  const txnColumns = useMemo(
    () => [
      { name: "Date", cell: (r) => formatDate(r.txn_date), width: "110px" },
      {
        name: "Code",
        selector: (r) => r.tax_code,
        width: "90px",
        cell: (r) => <span className="credora-tax-vat__code">{r.tax_code}</span>,
      },
      { name: "Reference", selector: (r) => r.reference, width: "120px" },
      { name: "Description", selector: (r) => r.description, grow: 2 },
      {
        name: "Base",
        cell: (r) => <span className="credora-tax-vat__amount">{formatTZS(r.base_amount)}</span>,
        right: true,
        width: "130px",
      },
      {
        name: "Tax",
        cell: (r) => <span className="credora-tax-vat__amount">{formatTZS(r.tax_amount)}</span>,
        right: true,
        width: "130px",
      },
    ],
    []
  );

  const summaryColumns = useMemo(
    () => [
      {
        name: "Code",
        selector: (r) => r.code,
        width: "90px",
        cell: (r) => <span className="credora-tax-vat__code">{r.code}</span>,
      },
      { name: "Name", selector: (r) => r.name, grow: 2 },
      {
        name: "Type",
        selector: (r) => r.tax_type,
        width: "120px",
        cell: (r) => {
          const meta = TAX_TYPE_META[r.tax_type] || { label: r.tax_type, tone: "vat" };
          return <span className={`credora-tax-vat__type credora-tax-vat__type--${meta.tone}`}>{meta.label}</span>;
        },
      },
      { name: "Rate", selector: (r) => `${r.rate_pct}%`, width: "80px" },
      {
        name: "Base",
        cell: (r) => <span className="credora-tax-vat__amount">{formatTZS(r.base_amount)}</span>,
        right: true,
        width: "130px",
      },
      {
        name: "Tax",
        cell: (r) => <span className="credora-tax-vat__amount">{formatTZS(r.tax_amount)}</span>,
        right: true,
        width: "130px",
      },
    ],
    []
  );

  const tabLabels = useMemo(
    () =>
      TABS.map((t) => {
        if (t.id === "codes") return { ...t, label: `${t.label} (${codes.length})` };
        if (t.id === "transactions") return { ...t, label: `${t.label} (${txns.length})` };
        return t;
      }),
    [codes.length, txns.length]
  );

  const handleCreateCode = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () =>
          createTaxCode({
            branch_id: branchId,
            code: codeForm.code,
            name: codeForm.name,
            tax_type: codeForm.tax_type,
            rate_pct: Number(codeForm.rate_pct),
          }),
        { successMessage: "Tax code created." }
      );
      setCodeModal(false);
      setCodeForm({ code: "", name: "", tax_type: "vat", rate_pct: "18" });
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTxn = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () =>
          createTaxTransaction({
            branch_id: branchId,
            tax_code_id: txnForm.tax_code_id,
            txn_date: txnForm.txn_date,
            reference: txnForm.reference,
            description: txnForm.description,
            base_amount: Number(txnForm.base_amount),
          }),
        { successMessage: "Tax transaction recorded." }
      );
      setTxnModal(false);
      setTxnForm({
        tax_code_id: "",
        txn_date: todayIsoDate(),
        reference: "",
        description: "",
        base_amount: "",
      });
      load();
    } finally {
      setSaving(false);
    }
  };

  const hasData = summary || codes.length > 0 || txns.length > 0;

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Tax & VAT" />
      <Container fluid className="credora-tax-vat credora-table-page pb-5 mb-3">
        <div className="credora-tax-vat__toolbar">
          <div>
            <h4 className="credora-tax-vat__title">Tax & VAT</h4>
            <p className="credora-tax-vat__subtitle mb-0">
              Configure tax codes, record taxable transactions, and review VAT summaries for compliance reporting.
            </p>
          </div>
          <div className="credora-tax-vat__controls">
            <Input
              type="select"
              bsSize="sm"
              className="credora-tax-vat__branch-select"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Input>
            <Button color="light" size="sm" className="border" onClick={() => runRefresh(load, { label: "Tax & VAT" })} disabled={loading}>
              Refresh
            </Button>
            <Button color="light" size="sm" className="border" onClick={() => setCodeModal(true)}>
              <CredoraIcon name="plus" size={14} className="me-1" />
              New tax code
            </Button>
            <Button color="primary" size="sm" onClick={() => setTxnModal(true)} disabled={!activeCodes}>
              <CredoraIcon name="plus" size={14} className="me-1" />
              Record transaction
            </Button>
          </div>
        </div>

        {error && <Alert color="danger">{error}</Alert>}

        {loading && !hasData ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <Row className="credora-table-page__metrics g-3 mb-3">
              <Col sm="6" xl="3">
                <TablePageMetricCard
                  title="Taxable base"
                  value={formatCompactTZS(summary?.total_base || 0)}
                  icon="sale"
                  iconClassName="svg-fill"
                  iconTone="primary"
                  subtitle={periodLabel}
                  trendLabel={branchName || "Branch"}
                  trendTone="info"
                />
              </Col>
              <Col sm="6" xl="3">
                <TablePageMetricCard
                  title="Total tax"
                  value={formatCompactTZS(summary?.total_tax || 0)}
                  icon="profit"
                  iconClassName="svg-fill"
                  iconTone="success"
                  subtitle={`${effectiveRatePct.toFixed(1)}% effective rate`}
                  trendLabel={`${summary?.rows?.length || 0} codes in period`}
                  trendTone="success"
                />
              </Col>
              <Col sm="6" xl="3">
                <TablePageMetricCard
                  title="Active tax codes"
                  value={activeCodes}
                  icon="form"
                  iconClassName="svg-stroke"
                  iconTone="warning"
                  subtitle={`${codes.length} total configured`}
                  progressPct={codes.length ? (activeCodes / codes.length) * 100 : 0}
                  progressTone="warning"
                />
              </Col>
              <Col sm="6" xl="3">
                <TablePageMetricCard
                  title="Transactions"
                  value={txns.length.toLocaleString()}
                  icon="table"
                  iconClassName="svg-stroke"
                  iconTone="info"
                  subtitle="All recorded tax entries"
                />
              </Col>
            </Row>

            <div className="credora-tax-vat__tabs-row">
              <div className="credora-tax-vat__tabs">
                {tabLabels.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`credora-tax-vat__tab ${tab === t.id ? "is-active" : ""}`}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {loading && (
                <span className="text-muted small d-inline-flex align-items-center gap-1">
                  <Spinner size="sm" /> Updating…
                </span>
              )}
            </div>

            <TabContent activeTab={tab}>
              <TabPane tabId="summary">
                <div className="card credora-tax-vat__filter-card">
                  <div className="card-body">
                    <Row className="g-3 align-items-end">
                      <Col md="3" sm="6">
                        <FormGroup className="mb-0">
                          <Label>From</Label>
                          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                        </FormGroup>
                      </Col>
                      <Col md="3" sm="6">
                        <FormGroup className="mb-0">
                          <Label>To</Label>
                          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                        </FormGroup>
                      </Col>
                      <AccountingFilterApplyButton
                        onClick={load}
                        loading={loading}
                        label="Apply period"
                      />
                    </Row>
                  </div>
                </div>

                <div className="credora-tax-vat__panel">
                  <div className="credora-tax-vat__panel-head">
                    <div>
                      <h5 className="mb-0">VAT summary by tax code</h5>
                      <p>
                        {periodLabel}
                        {branchName ? ` · ${branchName}` : ""}
                      </p>
                    </div>
                  </div>
                  {summary?.rows?.length ? (
                    <>
                      <DataTable
                        columns={summaryColumns}
                        data={summary.rows}
                        customStyles={DEFAULT_TABLE_CUSTOM_STYLES}
                        pagination
                        dense
                        noHeader
                      />
                      <div className="credora-tax-vat__panel-foot">
                        <strong>Total base: {formatTZS(summary.total_base)}</strong>
                        <strong>Total tax: {formatTZS(summary.total_tax)}</strong>
                      </div>
                    </>
                  ) : (
                    <div className="credora-tax-vat__empty">No tax activity in the selected period.</div>
                  )}
                </div>
              </TabPane>

              <TabPane tabId="codes">
                <div className="credora-tax-vat__panel">
                  <div className="credora-tax-vat__panel-head">
                    <div>
                      <h5 className="mb-0">Tax codes</h5>
                      <p>VAT, withholding, and sales tax rates linked to this branch</p>
                    </div>
                    <Button color="primary" size="sm" onClick={() => setCodeModal(true)}>
                      Add code
                    </Button>
                  </div>
                  {codes.length ? (
                    <DataTable columns={codeColumns} data={codes} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination dense noHeader />
                  ) : (
                    <div className="credora-tax-vat__empty">No tax codes yet. Create one to start recording transactions.</div>
                  )}
                </div>
              </TabPane>

              <TabPane tabId="transactions">
                <div className="credora-tax-vat__panel">
                  <div className="credora-tax-vat__panel-head">
                    <div>
                      <h5 className="mb-0">Tax transactions</h5>
                      <p>Recorded taxable amounts and computed tax by reference</p>
                    </div>
                    <Button color="primary" size="sm" onClick={() => setTxnModal(true)} disabled={!activeCodes}>
                      Record transaction
                    </Button>
                  </div>
                  {txns.length ? (
                    <DataTable columns={txnColumns} data={txns} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination dense noHeader />
                  ) : (
                    <div className="credora-tax-vat__empty">No transactions recorded yet.</div>
                  )}
                </div>
              </TabPane>
            </TabContent>
          </>
        )}

        <Modal isOpen={codeModal} toggle={() => setCodeModal(false)}>
          <Form onSubmit={handleCreateCode}>
            <ModalHeader toggle={() => setCodeModal(false)}>New tax code</ModalHeader>
            <ModalBody>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Code</Label>
                    <Input required value={codeForm.code} onChange={(e) => setCodeForm({ ...codeForm, code: e.target.value })} placeholder="VAT18" />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Name</Label>
                    <Input required value={codeForm.name} onChange={(e) => setCodeForm({ ...codeForm, name: e.target.value })} placeholder="Standard VAT 18%" />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Type</Label>
                    <Input type="select" value={codeForm.tax_type} onChange={(e) => setCodeForm({ ...codeForm, tax_type: e.target.value })}>
                      <option value="vat">VAT</option>
                      <option value="withholding">Withholding</option>
                      <option value="sales">Sales tax</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Rate %</Label>
                    <Input type="number" min="0.01" max="100" step="0.01" required value={codeForm.rate_pct} onChange={(e) => setCodeForm({ ...codeForm, rate_pct: e.target.value })} />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setCodeModal(false)}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={saving}>
                Save
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        <Modal isOpen={txnModal} toggle={() => setTxnModal(false)}>
          <Form onSubmit={handleCreateTxn}>
            <ModalHeader toggle={() => setTxnModal(false)}>Record tax transaction</ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Tax code</Label>
                <Input type="select" required value={txnForm.tax_code_id} onChange={(e) => setTxnForm({ ...txnForm, tax_code_id: e.target.value })}>
                  <option value="">Select code</option>
                  {codes
                    .filter((c) => c.is_active)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.code} — {c.name} ({c.rate_pct}%)
                      </option>
                    ))}
                </Input>
              </FormGroup>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Date</Label>
                    <Input type="date" required value={txnForm.txn_date} onChange={(e) => setTxnForm({ ...txnForm, txn_date: e.target.value })} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Reference</Label>
                    <Input required value={txnForm.reference} onChange={(e) => setTxnForm({ ...txnForm, reference: e.target.value })} placeholder="INV-2026-001" />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>Description</Label>
                    <Input required value={txnForm.description} onChange={(e) => setTxnForm({ ...txnForm, description: e.target.value })} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Base amount</Label>
                    <Input type="number" min="0.01" step="0.01" required value={txnForm.base_amount} onChange={(e) => setTxnForm({ ...txnForm, base_amount: e.target.value })} />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setTxnModal(false)}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={saving}>
                Save
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </Container>
    </Fragment>
  );
};

export default TaxVatPage;
